import React, { useState, useEffect } from 'react';
import { ref, onValue, push, set, remove, update } from 'firebase/database';
import { database } from '../../../config/firebase';
import PropTypes from 'prop-types';
import { PlusCircle, Calendar, Square, CheckSquare, Edit2, Trash2 } from 'lucide-react';
import { ConfirmDialog } from '../../modals';

const HomeworkByDate = ({ selectedStudent, currentUser }) => {
  const [homeworkByDate, setHomeworkByDate] = useState({});
  const [isAddingDate, setIsAddingDate] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [addingHomeworkDate, setAddingHomeworkDate] = useState(null);
  const [editingDate, setEditingDate] = useState(null);
  const [newDateValue, setNewDateValue] = useState('');

  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editTaskSubject, setEditTaskSubject] = useState('');
  const [editTaskContent, setEditTaskContent] = useState('');
  const [editTaskDueDate, setEditTaskDueDate] = useState('');
  const [editTaskComment, setEditTaskComment] = useState('');
  const [taskError, setTaskError] = useState('');

  // 新しい宿題の状態
  const [newHomework, setNewHomework] = useState({
    subject: '',
    content: '',
    dueDate: '',
    teacherComment: '',
  });

  // ConfirmDialog用の状態
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [onConfirmAction, setOnConfirmAction] = useState(() => {});

  // 教科のリストを定義
  const subjects = ['英語', '数学', '算数', '国語', '理科', '社会', 'その他'];

  // 生徒IDの決定
  const studentId = currentUser?.role === 'student' ? currentUser.id : selectedStudent?.id;

  // 曜日の取得
  const getJapaneseDayOfWeek = (dateStr) => {
    const days = ['日', '月', '火', '水', '木', '金', '土'];
    const date = new Date(dateStr);
    return days[date.getDay()];
  };

  // 日付のフォーマット（例: "3月15日(水)"）
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const dayOfWeek = getJapaneseDayOfWeek(dateStr);
    return `${month}月${day}日(${dayOfWeek})`;
  };

  // 期限のフォーマット（例: "3月15日(水)まで"）
  const formatDueDate = (dateStr) => {
    const date = new Date(dateStr);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const dayOfWeek = getJapaneseDayOfWeek(dateStr);
    return `${month}月${day}日(${dayOfWeek})まで`;
  };

  // 期限切れ判定
  const isOverdue = (dueDateStr) => {
    if (!dueDateStr) return false;
    const dueDate = new Date(dueDateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dueDate < today;
  };

  // データの取得
  useEffect(() => {
    if (!studentId) {
      setHomeworkByDate({});
      return;
    }

    const homeworkRef = ref(database, `homework/${studentId}`);
    const unsubscribe = onValue(homeworkRef, (snapshot) => {
      const data = snapshot.val() || {};
      setHomeworkByDate(data);
    });

    return () => unsubscribe();
  }, [studentId]);

  // 宿題の追加
  const handleAddHomework = async (date) => {
    if (!newHomework.subject.trim() || !newHomework.content.trim()) {
      alert('教科と内容を入力してください。');
      return;
    }

    const homeworkRef = push(ref(database, `homework/${studentId}/${date}/tasks`));
    await set(homeworkRef, {
      id: homeworkRef.key,
      subject: newHomework.subject,
      content: newHomework.content,
      dueDate: newHomework.dueDate,
      completed: false,
      teacherCheck: null,
      teacherComment: newHomework.teacherComment || '',
    });

    setNewHomework({ subject: '', content: '', dueDate: '', teacherComment: '' });
    setAddingHomeworkDate(null);
  };

  // 宿題の編集
  const handleUpdateHomework = async (date, homeworkId, updatedHomework) => {
    await update(ref(database, `homework/${studentId}/${date}/tasks/${homeworkId}`), updatedHomework);
    setEditingTaskId(null);
    setEditTaskSubject('');
    setEditTaskContent('');
    setEditTaskDueDate('');
    setEditTaskComment('');
    setTaskError('');
  };

  // 宿題の削除（ConfirmDialogを使用）
  const handleDeleteHomework = (date, homeworkId) => {
    setConfirmTitle('宿題の削除確認');
    setConfirmMessage('この宿題を削除してもよろしいですか？');
    setOnConfirmAction(() => async () => {
      await remove(ref(database, `homework/${studentId}/${date}/tasks/${homeworkId}`));
      setIsConfirmOpen(false);
    });
    setIsConfirmOpen(true);
  };

  // 日付ごとの宿題を一括で削除（ConfirmDialogを使用）
  const handleDeleteDate = (date) => {
    setConfirmTitle('日付ごとの宿題削除確認');
    setConfirmMessage(`"${formatDate(date)}"のすべての宿題を削除してもよろしいですか？`);
    setOnConfirmAction(() => async () => {
      await remove(ref(database, `homework/${studentId}/${date}`));
      setIsConfirmOpen(false);
    });
    setIsConfirmOpen(true);
  };

  // 完了状態の切り替え
  const handleToggleComplete = async (date, homeworkId, currentState) => {
    await update(ref(database, `homework/${studentId}/${date}/tasks/${homeworkId}`), {
      completed: !currentState
    });
  };

  // 教師チェックの更新
  const handleTeacherCheck = async (date, homeworkId, status) => {
    const currentCheck = homeworkByDate[date]?.tasks[homeworkId]?.teacherCheck;
    await update(ref(database, `homework/${studentId}/${date}/tasks/${homeworkId}`), {
      teacherCheck: currentCheck === status ? null : status
    });
  };

  // 日付の追加
  const handleAddDate = async () => {
    if (!selectedDate) {
      alert('日付を選択してください。');
      return;
    }

    if (homeworkByDate[selectedDate]) {
      alert('この日付の宿題は既に存在します。');
      return;
    }

    const newDate = selectedDate;
    await set(ref(database, `homework/${studentId}/${newDate}`), {
      date: newDate,
      tasks: {}
    });

    setSelectedDate('');
    setIsAddingDate(false);
    setAddingHomeworkDate(newDate);
  };

  // 「C追加」ボタンをクリックした際の処理
  const handleCopyAddDate = async () => {
    if (!selectedDate) {
      alert('日付を選択してください。');
      return;
    }

    if (homeworkByDate[selectedDate]) {
      alert('この日付の宿題は既に存在します。');
      return;
    }

    const dates = Object.keys(homeworkByDate);
    if (dates.length === 0) {
      alert('コピーする宿題がありません。');
      return;
    }

    const sortedDates = [...dates].sort((a, b) => new Date(b) - new Date(a));
    const lastDate = sortedDates[0];
    const lastTasks = homeworkByDate[lastDate]?.tasks || {};

    const newDate = selectedDate;
    await set(ref(database, `homework/${studentId}/${newDate}`), {
      date: newDate,
      tasks: {}
    });

    for (const taskId in lastTasks) {
      const task = lastTasks[taskId];
      const newTaskRef = push(ref(database, `homework/${studentId}/${newDate}/tasks`));
      await set(newTaskRef, {
        id: newTaskRef.key,
        subject: task.subject,
        content: task.content,
        dueDate: task.dueDate,
        completed: false,
        teacherCheck: null,
        teacherComment: task.teacherComment || '',
      });
    }

    setSelectedDate('');
    setIsAddingDate(false);
  };

  // 日付の編集
  const handleEditDate = (oldDate) => {
    setEditingDate(oldDate);
    setNewDateValue(oldDate);
  };

  const handleSaveDate = async () => {
    if (!newDateValue || newDateValue === editingDate) {
      setEditingDate(null);
      setNewDateValue('');
      return;
    }

    if (homeworkByDate[newDateValue]) {
      alert('この日付の宿題は既に存在します。');
      return;
    }

    const oldData = homeworkByDate[editingDate];
    await set(ref(database, `homework/${studentId}/${newDateValue}`), oldData);
    await remove(ref(database, `homework/${studentId}/${editingDate}`));

    setEditingDate(null);
    setNewDateValue('');
  };

  const handleCancelEditDate = () => {
    setEditingDate(null);
    setNewDateValue('');
  };

  // 宿題の編集開始
  const handleEditTask = (date, task) => {
    setEditingTaskId(task.id);
    setEditTaskSubject(task.subject);
    setEditTaskContent(task.content);
    setEditTaskDueDate(task.dueDate || '');
    setEditTaskComment(task.teacherComment || '');
  };

  // 宿題の編集保存
  const handleSaveTask = async (date, taskId) => {
    if (!editTaskSubject.trim() || !editTaskContent.trim()) {
      setTaskError('教科と内容を入力してください。');
      return;
    }

    const updatedHomework = {
      subject: editTaskSubject,
      content: editTaskContent,
      dueDate: editTaskDueDate,
      teacherComment: editTaskComment,
    };

    await handleUpdateHomework(date, taskId, updatedHomework);
  };

  // 宿題の編集キャンセル
  const handleCancelEditTask = () => {
    setEditingTaskId(null);
    setEditTaskSubject('');
    setEditTaskContent('');
    setEditTaskDueDate('');
    setEditTaskComment('');
    setTaskError('');
  };

  // 日付を降順でソート
  const sortedDates = Object.keys(homeworkByDate).sort((a, b) => new Date(b) - new Date(a));

  return (
    <div className="bg-white rounded-lg shadow-md p-3 sm:p-4">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-purple-800">日付別宿題管理</h2>
        <button
          onClick={() => setIsAddingDate(true)}
          className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center gap-2"
        >
          <PlusCircle size={20} />
          日付追加
        </button>
      </div>

      {isAddingDate && (
        <div className="bg-purple-50 p-3 rounded-lg mb-6">
          <h3 className="text-lg font-semibold text-purple-800 mb-3">新しい日付を追加</h3>
          <div className="flex gap-3 items-center">
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="border border-purple-300 rounded px-3 py-2"
            />
            <button
              onClick={handleAddDate}
              className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
            >
              追加
            </button>
            <button
              onClick={handleCopyAddDate}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              C追加
            </button>
            <button
              onClick={() => {
                setIsAddingDate(false);
                setSelectedDate('');
              }}
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
            >
              キャンセル
            </button>
          </div>
        </div>
      )}

      {sortedDates.map((date) => (
        <div key={date} className="mb-6 border border-purple-200 rounded-lg p-3">
          <div className="flex justify-between items-center mb-4 gap-1">
            {editingDate === date ? (
              <div className="space-y-3">
                {/* 上段: 日付入力フィールド */}
                <div>
                  <input
                    type="date"
                    value={newDateValue}
                    onChange={(e) => setNewDateValue(e.target.value)}
                    className="border border-purple-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
                
                {/* 下段: アクションボタン */}
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={handleSaveDate}
                    className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700 min-h-[44px] flex items-center justify-center whitespace-nowrap"
                  >
                    保存
                  </button>
                  <button
                    onClick={handleCancelEditDate}
                    className="bg-gray-500 text-white px-4 py-2 rounded text-sm hover:bg-gray-600 min-h-[44px] flex items-center justify-center whitespace-nowrap"
                  >
                    キャンセル
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-1 flex-1 min-w-0">
                <h3 className="text-base sm:text-lg font-semibold text-purple-800 flex items-center gap-1 bg-purple-50 px-2 py-2 rounded-lg flex-nowrap whitespace-nowrap w-fit">
                  <Calendar size={16} className="text-purple-600 flex-shrink-0" />
                  <span className="truncate">{formatDate(date)}</span>
                </h3>
                <button
                  onClick={() => handleEditDate(date)}
                  className="p-1 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded min-w-[40px] min-h-[44px] flex items-center justify-center flex-shrink-0"
                  aria-label="日付を編集"
                  title="日付を編集"
                >
                  <Edit2 size={14} />
                </button>
              </div>
            )}
            <div className="flex gap-1 flex-shrink-0">
              <button
                onClick={() => setAddingHomeworkDate(addingHomeworkDate === date ? null : date)}
                className="bg-purple-600 text-white px-2 py-2 rounded text-xs hover:bg-purple-700 flex items-center gap-1 min-w-[44px] min-h-[44px] justify-center"
                title="宿題追加"
              >
                <PlusCircle size={16} className="flex-shrink-0" />
                <span className="hidden md:inline text-xs">追加</span>
              </button>
              <button
                onClick={() => handleDeleteDate(date)}
                className="bg-red-600 text-white px-2 py-2 rounded text-xs hover:bg-red-700 flex items-center gap-1 min-w-[44px] min-h-[44px] justify-center"
                title="日付削除"
              >
                <Trash2 size={16} className="flex-shrink-0" />
                <span className="hidden md:inline text-xs">削除</span>
              </button>
            </div>
          </div>

          {addingHomeworkDate === date && (
            <div className="bg-purple-50 p-3 rounded-lg mb-4">
              <h4 className="text-lg font-semibold text-purple-800 mb-3">新しい宿題を追加</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-purple-700 mb-1">教科</label>
                  <select
                    value={newHomework.subject}
                    onChange={(e) => setNewHomework({ ...newHomework, subject: e.target.value })}
                    className="w-full border border-purple-300 rounded px-3 py-2"
                  >
                    <option value="">教科を選択</option>
                    {subjects.map((subject) => (
                      <option key={subject} value={subject}>
                        {subject}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-purple-700 mb-1">期限</label>
                  <input
                    type="date"
                    value={newHomework.dueDate}
                    onChange={(e) => setNewHomework({ ...newHomework, dueDate: e.target.value })}
                    className="w-full border border-purple-300 rounded px-3 py-2"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-purple-700 mb-1">内容</label>
                  <textarea
                    value={newHomework.content}
                    onChange={(e) => setNewHomework({ ...newHomework, content: e.target.value })}
                    className="w-full border border-purple-300 rounded px-3 py-2 h-24 break-words resize-y"
                    placeholder="宿題の内容を入力してください"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-purple-700 mb-1">先生のコメント</label>
                  <textarea
                    value={newHomework.teacherComment}
                    onChange={(e) => setNewHomework({ ...newHomework, teacherComment: e.target.value })}
                    className="w-full border border-purple-300 rounded px-3 py-2 h-20 break-words resize-y"
                    placeholder="先生からのコメント（任意）"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => handleAddHomework(date)}
                  className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 min-h-[44px] flex items-center justify-center"
                >
                  追加
                </button>
                <button
                  onClick={() => {
                    setAddingHomeworkDate(null);
                    setNewHomework({ subject: '', content: '', dueDate: '', teacherComment: '' });
                  }}
                  className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 min-h-[44px] flex items-center justify-center"
                >
                  キャンセル
                </button>
              </div>
            </div>
          )}

          <div className="space-y-3">
            {homeworkByDate[date]?.tasks &&
              Object.values(homeworkByDate[date].tasks).map((task) => (
                <div key={task.id} className={`bg-white rounded-lg p-3 overflow-hidden border-l-4 ${
                  task.completed 
                    ? 'border-green-500 bg-green-50' 
                    : isOverdue(task.dueDate) 
                    ? 'border-red-500 bg-red-50' 
                    : 'border-purple-500 bg-white'
                } border border-purple-200`}>
                  {editingTaskId === task.id ? (
                    <div>
                      {taskError && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded mb-3 text-sm">
                          {taskError}
                        </div>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-purple-700 mb-1">教科</label>
                          <select
                            value={editTaskSubject}
                            onChange={(e) => setEditTaskSubject(e.target.value)}
                            className="w-full border border-purple-300 rounded px-3 py-2"
                          >
                            <option value="">教科を選択</option>
                            {subjects.map((subject) => (
                              <option key={subject} value={subject}>
                                {subject}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-purple-700 mb-1">期限</label>
                          <input
                            type="date"
                            value={editTaskDueDate}
                            onChange={(e) => setEditTaskDueDate(e.target.value)}
                            className="w-full border border-purple-300 rounded px-3 py-2"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-purple-700 mb-1">内容</label>
                          <textarea
                            value={editTaskContent}
                            onChange={(e) => setEditTaskContent(e.target.value)}
                            className="w-full border border-purple-300 rounded px-3 py-2 h-24 break-words resize-y"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-purple-700 mb-1">先生のコメント</label>
                          <textarea
                            value={editTaskComment}
                            onChange={(e) => setEditTaskComment(e.target.value)}
                            className="w-full border border-purple-300 rounded px-3 py-2 h-20 break-words resize-y"
                          />
                        </div>
                      </div>
                      <div className="flex gap-3 mt-4">
                        <button
                          onClick={() => handleSaveTask(date, task.id)}
                          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 min-h-[44px] flex items-center justify-center"
                        >
                          保存
                        </button>
                        <button
                          onClick={handleCancelEditTask}
                          className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 min-h-[44px] flex items-center justify-center"
                        >
                          キャンセル
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="mb-2">
                        {/* 1行目: チェックボックス・教科名・編集/削除ボタン */}
                        <div className="flex justify-between items-center leading-tight">
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => handleToggleComplete(date, task.id, task.completed)}
                              className="p-2 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded min-w-[44px] min-h-[44px] flex items-center justify-center"
                              aria-label={task.completed ? "完了を取り消し" : "完了にする"}
                            >
                              {task.completed ? <CheckSquare size={20} /> : <Square size={20} />}
                            </button>
                            <div className="font-semibold text-purple-800">{task.subject}</div>
                          </div>
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleEditTask(date, task)}
                              className="p-2 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded min-w-[44px] min-h-[44px] flex items-center justify-center"
                              aria-label="編集"
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteHomework(date, task.id)}
                              className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded min-w-[44px] min-h-[44px] flex items-center justify-center"
                              aria-label="削除"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                        {/* 2行目: 期限 */}
                        {task.dueDate && (
                          <div className={`text-sm font-medium -mt-1 ${
                            isOverdue(task.dueDate) 
                              ? 'text-red-600' 
                              : 'text-purple-600'
                          }`}>
                            {isOverdue(task.dueDate) ? (
                              <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded-full text-xs font-semibold mr-2">
                                期限切れ
                              </span>
                            ) : null}
                            <span className="sm:hidden">
                              {(() => {
                                const date = new Date(task.dueDate);
                                const month = date.getMonth() + 1;
                                const day = date.getDate();
                                const dayOfWeek = getJapaneseDayOfWeek(task.dueDate);
                                return `${month}/${day}(${dayOfWeek})まで`;
                              })()}
                            </span>
                            <span className="hidden sm:inline">
                              ({formatDueDate(task.dueDate)})
                            </span>
                          </div>
                        )}
                      </div>
                      <div className={`border p-3 rounded-lg mb-2 ${
                        task.completed 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-yellow-50 border-yellow-200'
                      }`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`font-medium text-sm ${
                            task.completed 
                              ? 'text-green-700' 
                              : 'text-yellow-700'
                          }`}>
                            📝 宿題内容
                          </span>
                        </div>
                        <p className={`text-sm break-words overflow-wrap-anywhere leading-relaxed ${
                          task.completed 
                            ? 'line-through text-green-600' 
                            : 'text-yellow-800'
                        }`}>
                          {task.completed && '✅ '}
                          {task.content}
                        </p>
                      </div>
                      {task.teacherComment && (
                        <div className="bg-purple-50 border border-purple-200 p-3 rounded-lg mt-2">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-purple-700 font-medium text-sm">💬 先生のコメント</span>
                          </div>
                          <p className="text-sm text-purple-800 break-words overflow-wrap-anywhere leading-relaxed">
                            {task.teacherComment}
                          </p>
                        </div>
                      )}
                      {currentUser?.role === 'teacher' && (
                        <div className="flex gap-3 mt-3">
                          <button
                            onClick={() => handleTeacherCheck(date, task.id, 'maru')}
                            className={`px-4 py-2 rounded text-sm font-medium min-w-[44px] min-h-[44px] flex items-center justify-center ${
                              task.teacherCheck === 'maru'
                                ? 'bg-yellow-300 text-yellow-800'
                                : 'bg-purple-200 text-purple-600 hover:bg-purple-300'
                            }`}
                            aria-label="合格"
                          >
                            ○
                          </button>
                          <button
                            onClick={() => handleTeacherCheck(date, task.id, 'triangle')}
                            className={`px-4 py-2 rounded text-sm font-medium min-w-[44px] min-h-[44px] flex items-center justify-center ${
                              task.teacherCheck === 'triangle'
                                ? 'bg-purple-300 text-purple-800'
                                : 'bg-purple-200 text-purple-600 hover:bg-purple-300'
                            }`}
                            aria-label="要改善"
                          >
                            △
                          </button>
                          <button
                            onClick={() => handleTeacherCheck(date, task.id, 'batsu')}
                            className={`px-4 py-2 rounded text-sm font-medium min-w-[44px] min-h-[44px] flex items-center justify-center ${
                              task.teacherCheck === 'batsu'
                                ? 'bg-pink-300 text-pink-800'
                                : 'bg-purple-200 text-purple-600 hover:bg-purple-300'
                            }`}
                            aria-label="やり直し"
                          >
                            ×
                          </button>
                        </div>
                      )}
                      {currentUser?.role === 'student' && task.teacherCheck && (
                        <div className={`mt-2 px-3 py-2 rounded-lg text-sm font-medium ${
                          task.teacherCheck === 'maru' 
                            ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' :
                          task.teacherCheck === 'triangle' 
                            ? 'bg-purple-100 text-purple-800 border border-purple-200' : 
                            'bg-pink-100 text-pink-800 border border-pink-200'
                        }`}>
                          <span className="flex items-center gap-2">
                            先生確認:
                            <span className="text-lg font-bold">
                              {task.teacherCheck === 'maru' ? '○ 合格' : 
                               task.teacherCheck === 'triangle' ? '△ 要改善' : '× やり直し'}
                            </span>
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>
      ))}

      {sortedDates.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          まだ宿題が登録されていません。「日付追加」ボタンから始めてください。
        </div>
      )}

      {isConfirmOpen && (
        <ConfirmDialog
          title={confirmTitle}
          message={confirmMessage}
          onConfirm={onConfirmAction}
          onCancel={() => setIsConfirmOpen(false)}
        />
      )}
    </div>
  );
};

HomeworkByDate.propTypes = {
  selectedStudent: PropTypes.object,
  currentUser: PropTypes.object.isRequired,
};

export default HomeworkByDate; 